import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { UserCheck, Info, Search } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import usePageTitle from '../hooks/usePageTitle';
import { r16mUiEnabled } from '../lib/r14Switch';
import { useOptionalAuth } from '../contexts/AuthContext';
import { followBackApi } from '../lib/api';
import { r14ErrorText } from '../lib/r14Errors';
import {
  normaliseFollowBack, headline, sourceRows, bestOf, targetRanking, trendRows, heroSpark, normaliseBatches, scopeFromParams, normaliseSlotList,
  fmtRatePct, FB_TAG, PAGE_RANGES, classifyFbError, stuckSlots, notReporting,
} from '../lib/followBackView';
import { Segmented, EmptyPanel } from '../components/stats/ChartKit';
import { CompactLine } from '../components/stats/R15Parts';
import {
  SignalStrip, KpiRow, TrendCard, TargetCard, EmptyNotice, NoSignalNote, TargetChip, StuckAlert, rangeOptions, fbLog,
} from '../components/stats/FollowBackParts';
import { SlotsCard, BatchPanel } from '../components/stats/FollowBackGroup';
import useIsPhone from '../lib/useIsPhone';
import { fmtSpan } from '../lib/slotStatsView';
import '../components/stats/stats.css';
import useDoctorMe from '../hooks/useDoctorMe';
import { doctorUiOn } from '../lib/doctorSwitch';
import FollowBackDoctorNote from '../components/doctor/FollowBackDoctorNote';

/*
 * r16m (2026-10-20), design 3.7 "Group": the Follow-back page at /follow-back (nav: Growth > Follow-back).
 *
 * Scope: a batch (GET /uiapi/follow-back/batches - the explicit batch records, design 2.4) or a slot range; the URL carries it as
 * ?batch=<id> or ?slots=801-805,901 (slot NUMBERS only, never a username), plus ?range=7|30|all and ?target=<a target account>.
 * Without a scope the first live batch is shown. Top to bottom: one filter row (range labelled with the cohort dates, the target
 * chip), the signal strip, the KPI row, the rate by source type over time with people engaged under it, the target ranking, the
 * per-slot table and chart, and (batch scope) the batch panel with Join / Detach / Retire. Every chart has a table twin.
 * Switch: lib/r14Switch.js r16mUiEnabled (OFF: the route goes to the dashboard and the nav item is gone).
 */

const RANGE_VALUES = new Set(['7', '30', 'all']);

/**
 * The signed-in account is an admin (/uiapi/me isAdmin, or the "owner" role). An admin's batch list holds EVERY account's batches
 * with no owner field, while Join / Detach / Retire act on the caller's own slots only: the page then offers no actions (a server
 * that marks a batch `own: true` gets them back). Outside an AuthProvider (a test): not an admin.
 */
function useIsAdmin() {
  const auth = useOptionalAuth();
  const user = auth && auth.user;
  return !!(user && (user.isAdmin === true || user.role === 'owner'));
}

function ScopePicker({ batches, scope, onBatch, onSlots }) {
  const [text, setText] = useState(scope && scope.slots ? scope.slots : '');
  useEffect(() => { setText(scope && scope.slots ? scope.slots : ''); }, [scope]);
  const live = batches.filter((b) => !b.retired);
  const old = batches.filter((b) => b.retired);
  const value = scope && scope.batch ? scope.batch : (scope && scope.slots ? '__slots' : '');
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="fb-scope">
      <label className="sr-only" htmlFor="fb-scope-batch">Batch</label>
      <select id="fb-scope-batch" value={value} onChange={(e) => { if (e.target.value && e.target.value !== '__slots') onBatch(e.target.value); }}
        className="h-9 max-w-full rounded-full border border-white/10 bg-secondary/60 px-3 text-sm text-foreground" data-testid="fb-scope-batch">
        {!value && <option value="">Pick a batch</option>}
        {live.map((b) => <option key={b.id} value={b.id}>{b.label}</option>)}
        {old.map((b) => <option key={b.id} value={b.id}>{`${b.label} (retired)`}</option>)}
        {value === '__slots' && <option value="__slots">Slots {scope.slots}</option>}
      </select>
      <form className="flex items-center gap-1.5" onSubmit={(e) => { e.preventDefault(); const s = normaliseSlotList(text); if (s) onSlots(s); }}>
        <label className="sr-only" htmlFor="fb-scope-slots">Slot numbers</label>
        <input id="fb-scope-slots" value={text} onChange={(e) => setText(e.target.value)} placeholder="or slots: 801-805, 901"
          className="h-9 w-44 rounded-full border border-white/10 bg-secondary/60 px-3 text-sm text-foreground placeholder:text-muted-foreground"
          data-testid="fb-scope-slots" inputMode="text" autoComplete="off" />
        <button type="submit" className="tap inline-flex h-9 items-center gap-1 rounded-full border border-white/10 px-3 text-sm text-foreground hover:bg-white/5"
          data-testid="fb-scope-slots-go">
          <Search className="h-3.5 w-3.5" aria-hidden />Show
        </button>
      </form>
    </div>
  );
}

export default function FollowBackPage() {
  usePageTitle('Follow-back');
  const on = r16mUiEnabled();
  const isPhone = useIsPhone();
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const search = params.toString();
  const urlScope = useMemo(() => scopeFromParams(new URLSearchParams(search)), [search]);
  const rangeParam = params.get('range');
  const range = RANGE_VALUES.has(String(rangeParam)) ? (rangeParam === 'all' ? 'all' : Number(rangeParam)) : 30;
  const target = params.get('target') || null;

  const bq = useQuery({
    queryKey: ['followBackBatches'],
    queryFn: () => followBackApi.batches().then((r) => r.data),
    retry: false,
    enabled: on,
  });
  const batches = useMemo(() => (bq.data ? normaliseBatches(bq.data) : []), [bq.data]);
  // without a scope in the URL: the first live batch
  const firstLive = batches.find((b) => !b.retired);
  const firstLiveId = firstLive ? firstLive.id : null;
  const scope = useMemo(() => urlScope || (firstLiveId ? { batch: firstLiveId } : null), [urlScope, firstLiveId]);
  const groupKey = ['followBack', 'group', scope ? (scope.batch ? `b${scope.batch}` : `s${scope.slots}`) : '-', range, target || ''];
  const gq = useQuery({
    queryKey: groupKey,
    queryFn: () => followBackApi.group({ ...scope, range, target }).then((r) => r.data),
    enabled: on && !!scope,
    retry: false,
    placeholderData: (prev) => prev,
  });
  const m = useMemo(() => (gq.data ? normaliseFollowBack(gq.data) : null), [gq.data]);
  const view = useMemo(() => {
    if (!m) return null;
    const sources = sourceRows(m);
    const trend = trendRows(m);
    return { h: headline(m), sources, best: bestOf(sources), ranking: targetRanking(m), trend, spark: heroSpark(trend) };
  }, [m]);
  const batch = scope && scope.batch ? batches.find((b) => b.id === String(scope.batch)) || null : null;
  const stuck = stuckSlots(batch);
  // r16sd SD22 (lib/doctorSwitch.js part followBackNote): 92 off on the slots shown here, from their doctor panels
  const doctor = useDoctorMe();
  const fbDoctorOn = doctor.available && doctorUiOn('followBackNote');
  const toPending = () => {
    const el = typeof document !== 'undefined' ? document.getElementById('fb-batch-pending') : null;
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    fbLog('info', `${FB_TAG} page: the stuck-slot alert opened the batch panel's pending list (${stuck.map((p) => p.slot).join(', ')})`);
  };

  // a link to the batch panel's "Not in the batch" list (#fb-batch-pending: the slot card's alert, the #1217 hint) scrolls there once
  // the figures are drawn. The charts above the panel measure and draw one after the other (each pushes it down), so it looks again
  // twice and scrolls only while the list is not near the top yet (a scroll that already landed is left alone).
  const hashScroll = useRef({ done: false, timers: [] });
  useEffect(() => {
    const hs = hashScroll.current;
    return () => hs.timers.forEach(clearTimeout);
  }, []);
  useEffect(() => {
    const hs = hashScroll.current;
    if (hs.done || !batch || (!m && !gq.error)) return;
    if (typeof window === 'undefined' || !window.location || window.location.hash !== '#fb-batch-pending') return;
    hs.done = true;
    hs.timers = [150, 600, 1400].map((ms, i) => setTimeout(() => {
      const el = document.getElementById('fb-batch-pending');
      if (!el || !el.scrollIntoView) return;
      const top = el.getBoundingClientRect().top;
      if (i > 0 && top >= 0 && top < 160) return;
      el.scrollIntoView({ block: 'start' });
      fbLog('info', `${FB_TAG} page: opened at batch ${batch.id}'s "Not in the batch" list (#fb-batch-pending), look ${i + 1}`);
    }, ms));
  }, [batch, m, gq.error]);

  useEffect(() => {
    if (m && view) {
      fbLog('info', `${FB_TAG} page ${scope && scope.batch ? `batch ${scope.batch}` : `slots ${scope && scope.slots}`} range ${range}`
        + `${target ? ` target ${target}` : ''}: ${view.h.state}, engaged ${view.h.engaged}, rate ${fmtRatePct(view.h.rate)}, `
        + `${m.bySlot.length} slots, ${view.ranking.rows.length} targets`);
    }
  }, [m, view, scope, range, target]);

  if (!on) return <Navigate to="/dashboard" replace />;

  const setParam = (changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => { if (v === null || v === undefined || v === '') next.delete(k); else next.set(k, String(v)); });
    setParams(next);
  };
  const onBatch = (id) => setParam({ batch: id, slots: null, target: null });
  const onSlots = (s) => setParam({ slots: s, batch: null, target: null });
  const openSlot = (slot) => navigate(`/slots/${encodeURIComponent(slot)}/stats`);

  const header = (
    <PageHeader icon={UserCheck} title="Follow-back"
      description="Who followed back after being engaged, by the source they were picked from and by target account - per batch or slot range."
      actions={<ScopePicker batches={batches} scope={scope} onBatch={onBatch} onSlots={onSlots} />} />
  );

  let body;
  const bErr = bq.error ? classifyFbError(bq.error) : null;
  const gErr = gq.error ? classifyFbError(gq.error) : null;
  if (gErr && gErr.kind === 'routeMissing') {
    fbLog('info', `[R16M-UI] Follow-back page: no /follow-back route on this server (HTTP ${gErr.status})`);
    body = (
      <EmptyPanel testid="fb-page-no-route">
        This server has no follow-back statistics yet (it needs the r16m server). Nothing is shown rather than empty figures.
      </EmptyPanel>
    );
  } else if (gErr && gErr.kind === 'notEnabled') {
    body = <CompactLine icon={Info} title="Follow-back" testid="fb-page-off">{gErr.message || 'Follow-back stats are switched off on this server.'}</CompactLine>;
  } else if (gErr && (gErr.kind === 'notFound' || gErr.kind === 'slotNotFound' || gErr.kind === 'refused')) {
    // the server's own refusal (a batch or slot that is not this account's, a slot list it cannot read): its words
    fbLog('info', `[R16M-UI] Follow-back page: the server refused the scope (HTTP ${gErr.status} ${gErr.code}): ${gErr.message || '-'}`);
    body = <CompactLine icon={Info} title="Follow-back" tone="error" testid="fb-page-refused">{gErr.message || gErr.code}</CompactLine>;
  } else if (gq.error) {
    fbLog('warn', `[R16M-UI] Follow-back page figures could not be loaded: HTTP ${gErr.status ?? '-'} ${gErr.code || ''} ${gErr.message || ''}`.trim());
    body = <CompactLine icon={Info} title="Follow-back" tone="error" testid="fb-page-error">The figures could not be loaded ({r14ErrorText(gq.error, 'no answer')}).</CompactLine>;
  } else if (!scope) {
    body = (
      <EmptyPanel testid="fb-page-no-scope">
        {bq.isLoading ? 'Loading the batches...'
          : bErr && bErr.kind !== 'other'
            ? 'Type slot numbers above (for example 801-805, 901) to see their follow-back figures.'
            : 'No batch on this account yet. A batch is created at the first session of a slot with #1216 on; until then, type slot numbers above (for example 801-805, 901).'}
      </EmptyPanel>
    );
  } else if (gq.isLoading || !m || !view) {
    body = <p className="text-sm text-muted-foreground" data-testid="fb-page-loading">{gq.isLoading ? 'Loading follow-back figures...' : 'This answer carries no follow-back figures.'}</p>;
  } else {
    const { h } = view;
    const group = m.bySlot.length > 0 || m.kind !== 'slot';
    body = (
      <div className={`space-y-4 transition-opacity ${gq.isPlaceholderData ? 'opacity-60' : ''}`} aria-busy={gq.isPlaceholderData || undefined}
        data-testid="fb-page-body" data-state={h.state}>
        <SignalStrip m={m} group={group} testid="fb-signal" />
        {fbDoctorOn && <FollowBackDoctorNote slots={notReporting(m)} enabled />}
        <StuckAlert batch={batch} stuck={stuck} onJoin={toPending} testid="fb-stuck" />
        <EmptyNotice m={m} h={h} slotLabel="these slots" testid="fb-empty" />
        <NoSignalNote m={m} h={h} testid="fb-no-signal" />
        {h.figures && (
          <>
            <KpiRow m={m} h={h} sources={view.sources} best={view.best} ranking={view.ranking} spark={view.spark} group={group} />
            <TrendCard m={m} testid="fb-trend" />
            <TargetCard m={m} ranking={view.ranking} onPick={target ? null : (t) => setParam({ target: t })} testid="fb-targets" />
            {group && <SlotsCard m={m} batch={batch} onOpen={openSlot} testid="fb-slots" />}
          </>
        )}
        <p className="px-1 text-[11px] leading-relaxed text-muted-foreground text-pretty" data-testid="fb-definition">
          Follow-back rate = people who followed the engaging slot within {m.windowDays} days of its first engagement ÷ people it engaged,
          counted per person and per slot ({group ? 'a group rate is the sum over the sum, never an average of slot rates' : 'one slot'}).
          Matured periods only; a change is in percentage points against the period before, also matured. People already following, and
          engagements before the slot&apos;s follow-back tracking started, are left out of both sides. Times in {m.tzLabel}.
        </p>
      </div>
    );
  }

  return (
    <div className="stats-viz" data-testid="follow-back-page">
      {header}
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="fb-filters">
        <Segmented label="Range" options={rangeOptions(m, PAGE_RANGES, { short: isPhone })} value={range}
          onChange={(v) => setParam({ range: v === 30 ? null : v })} testidPrefix="fb-range" />
        {isPhone && view && view.h.days && view.h.days.from && (
          <span className="text-xs text-muted-foreground" data-testid="fb-range-days">engaged {fmtSpan(view.h.days.from, view.h.days.to)}</span>
        )}
        <TargetChip target={target} onClear={() => setParam({ target: null })} />
      </div>
      <div className="space-y-4">
        {body}
        {batch && (
          <BatchPanel batch={batch} groupKey={groupKey} testid="fb-batch" canAct={!admin || batch.own === true}
            unknown={m && range === 'all' && !target && m.kind === 'batch' ? m.totals.unknownOutcome : null} />
        )}
        {bErr && bErr.kind === 'other' && (
          <CompactLine icon={Info} title="Batches" tone="error" testid="fb-batches-error">The batch list could not be loaded ({r14ErrorText(bq.error, 'no answer')}).</CompactLine>
        )}
      </div>
    </div>
  );
}
